import { createContext, useContext, useEffect, type MutableRefObject } from 'react';

/**
 * Metadata that drives the single primary "Salvar" button rendered in the
 * sticky page header of the Account (Configurações) form.
 *
 * The page is multi-tab and each tab is an independent form/action, but the DS
 * form contract requires ONE primary button in the header ("ds-btn-primary
 * único"). Each tab publishes its own save handler + state here; the header
 * proxies clicks to whichever tab is currently mounted.
 */
export interface AccountSaveMeta {
  /** Whether the header should render the primary button (tabs w/o a save action hide it). */
  visible: boolean;
  /** Save in progress — button shows spinner + loadingLabel and is disabled. */
  saving: boolean;
  /** Extra disabled gate (independent from `saving`). */
  disabled: boolean;
  /** Idle button label. */
  label: string;
  /** Label while `saving`. */
  loadingLabel: string;
}

export interface AccountSaveContextValue {
  /** Latest save handler of the active tab (updated every render, no re-render). */
  onSaveRef: MutableRefObject<(() => void | Promise<void>) | null>;
  /** Publishes the display metadata for the header button. */
  setSaveMeta: (updater: AccountSaveMeta | ((prev: AccountSaveMeta) => AccountSaveMeta)) => void;
}

export const AccountSaveContext = createContext<AccountSaveContextValue | null>(null);

export interface UseAccountSaveButtonOptions {
  /** Called when the header primary button is clicked. */
  onSave: () => void | Promise<void>;
  /** Save in progress. */
  saving: boolean;
  /** Optional extra disabled gate. */
  disabled?: boolean;
  /** Idle button label (default "Salvar alterações"). */
  label?: string;
  /** Label while saving (default "Salvando..."). */
  loadingLabel?: string;
}

/**
 * Registers the active tab's primary "Salvar" action with the sticky page
 * header. Tabs that don't call this hook (e.g. Conexões MCP) leave the header
 * without a save button.
 */
export function useAccountSaveButton(options: UseAccountSaveButtonOptions): void {
  const ctx = useContext(AccountSaveContext);
  const { onSave, saving, disabled, label, loadingLabel } = options;

  // Keep the latest onSave handler in a ref so a click always runs current state.
  useEffect(() => {
    if (ctx) ctx.onSaveRef.current = onSave;
  });

  // Publish display-affecting metadata; hide the button when the tab unmounts.
  useEffect(() => {
    if (!ctx) return;
    ctx.setSaveMeta({
      visible: true,
      saving,
      disabled: disabled ?? false,
      label: label ?? 'Salvar alterações',
      loadingLabel: loadingLabel ?? 'Salvando...',
    });
    return () => {
      ctx.setSaveMeta((prev) => ({ ...prev, visible: false }));
    };
  }, [ctx, saving, disabled, label, loadingLabel]);
}
